<template>
  <component
    :is="tag"
    v-if="!dismissed"
    :class="[
      'inline-flex items-center justify-center whitespace-nowrap rounded border-none py-px text-xs font-medium',
      themeColorClass,
      tag === 'button' && themeInteractiveColorClass,
      { 'shadow-sm': theme === 'white' },
      rounded && 'rounded-full',
      small ? 'h-4' : 'h-5',
      pxClass,
    ]"
    :type="tag === 'button' ? 'button' : undefined"
  >
    <div class="flex items-center gap-1">
      <span :class="{ 'lowercase first-letter:uppercase': capitalized }"><slot /></span>
      <slot name="icon" />
    </div>
    <button v-if="dismissible" class="ml-1.5 flex" type="button" @click="dismiss">
      <x-icon class="text-inherit opacity-60 transition-opacity duration-300 icon-s hover:opacity-100" />
    </button>
  </component>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { XIcon } from '@klausapp/ui-kit/icons'
import { type UiTagThemeType, UiTagTheme } from '../types'

const props = withDefaults(
  defineProps<{
    theme?: UiTagThemeType
    dismissible?: boolean
    rounded?: boolean
    capitalized?: boolean
    tag?: 'span' | 'button'
    small?: boolean
  }>(),
  {
    theme: UiTagTheme.Default,
    dismissible: false,
    rounded: false,
    capitalized: true,
    tag: 'span',
  },
)

const emit = defineEmits<{
  dismiss: []
}>()

const TAG_COLORS: Record<UiTagThemeType, string> = {
  default: 'text-gray-80 bg-gray-20',
  brand: 'text-white bg-tag-brand-bg',
  'brand-light': 'text-tag-light-color bg-tag-light-bg',
  blue: 'text-white bg-blue-50',
  green: 'text-white bg-green-50',
  'green-light': 'text-green-70 bg-green-20',
  yellow: 'text-white bg-yellow-50',
  'yellow-light': 'text-yellow-80 bg-yellow-20',
  red: 'text-white bg-red-50',
  'red-light': 'text-red-70 bg-red-20',
  white: 'text-gray-80 bg-white',
  orange: 'text-orange-50 bg-orange-20',
  'orange-dark': 'text-orange-80 bg-orange-20',
  zennovate: 'text-gray-70 bg-white !border-solid !border-gray-30 h-6 border px-3 hover:bg-gray-10',
} as const

const TAG_INTERACTIVE_COLORS = {
  orange: 'hover:text-orange-60 hover:bg-orange-30',
} as const

const themeColorClass = computed(() => TAG_COLORS[props.theme])
const themeInteractiveColorClass = computed(() => TAG_INTERACTIVE_COLORS[props.theme])

const pxClass = computed(() => {
  if (props.small) return 'px-1'

  return props.rounded ? 'px-2' : 'px-1.5'
})

const dismissed = ref(false)

const dismiss = () => {
  dismissed.value = true
  emit('dismiss')
}
</script>
